
import React, { useState } from 'react';
import { MCPService } from '@/types';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Cpu, Edit, TrashIcon, Check, X, Cloud, Code, Image, 
  Calculator, Search, Languages, BarChart, Github, 
  TrendingUp, Mic, FileText, Database, ExternalLink, 
  Loader2, ShieldCheck, ShieldOff
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

interface MCPServiceCardProps {
  service: MCPService;
  apiKey?: string;
  isActivated?: boolean;
  onUpdate: (service: MCPService) => void;
  onDelete: (id: string) => void;
  onSaveApiKey: (apiKey: string) => Promise<boolean>;
}

const MCPServiceCard: React.FC<MCPServiceCardProps> = ({
  service,
  apiKey = '',
  isActivated = false,
  onUpdate,
  onDelete,
  onSaveApiKey,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentApiKey, setCurrentApiKey] = useState(apiKey);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const success = await onSaveApiKey(currentApiKey);
      if (success) {
        setIsEditing(false);
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  const getTierBadgeClass = () => {
    switch (service.tier) {
      case 'free':
        return 'bg-green-500/10 text-green-600';
      case 'standard':
        return 'bg-blue-500/10 text-blue-600';
      case 'enterprise':
        return 'bg-amber-500/10 text-amber-600';
      default:
        return 'bg-green-500/10 text-green-600';
    }
  };

  const getIconComponent = () => {
    switch (service.icon) {
      case 'cloud': return <Cloud size={18} className="text-primary" />;
      case 'code': return <Code size={18} className="text-primary" />;
      case 'image': return <Image size={18} className="text-primary" />;
      case 'calculator': return <Calculator size={18} className="text-primary" />;
      case 'search': return <Search size={18} className="text-primary" />;
      case 'languages': return <Languages size={18} className="text-primary" />;
      case 'bar-chart': return <BarChart size={18} className="text-primary" />;
      case 'github': return <Github size={18} className="text-primary" />;
      case 'trending-up': return <TrendingUp size={18} className="text-primary" />;
      case 'mic': return <Mic size={18} className="text-primary" />;
      case 'file-text': return <FileText size={18} className="text-primary" />;
      case 'database': return <Database size={18} className="text-primary" />;
      case 'external-link': return <ExternalLink size={18} className="text-primary" />;
      default: return <Cpu size={18} className="text-primary" />;
    }
  };
  
  return (
    <>
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-2">
              {getIconComponent()}
              <CardTitle className="text-lg">{service.name}</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <div className={`text-xs px-2 py-1 rounded-full ${getTierBadgeClass()}`}>
                {service.tier.charAt(0).toUpperCase() + service.tier.slice(1)}
              </div>
              {isActivated && (
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-200">
                  <ShieldCheck size={12} className="mr-1" />
                  Active
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="flex-grow">
          <p className="text-sm text-muted-foreground mb-4">{service.description}</p>
          
          <div className="space-y-3">
            <div className="text-sm">
              <span className="font-medium">Service URL:</span>{' '}
              <span className="text-muted-foreground break-all">{service.url}</span>
            </div>
            
            {!isEditing ? (
              <div className="text-sm">
                <span className="font-medium">API Key:</span>{' '}
                <span className="text-muted-foreground">
                  {apiKey 
                    ? `${apiKey.substring(0, 4)}...${apiKey.substring(apiKey.length - 4)}`
                    : 'Not set'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-6 w-6 p-0"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit size={14} />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  value={currentApiKey}
                  onChange={(e) => setCurrentApiKey(e.target.value)}
                  placeholder="Enter API key"
                  type="password"
                  className="h-8 text-sm"
                  disabled={isSaving}
                />
                {isSaving ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled
                  >
                    <Loader2 size={16} className="animate-spin" />
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleSave}
                    >
                      <Check size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setCurrentApiKey(apiKey);
                        setIsEditing(false);
                      }}
                    >
                      <X size={16} />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-between">
          <Button
            variant={isActivated ? "default" : "outline"}
            size="sm"
            className={isActivated ? "bg-green-600 hover:bg-green-700" : ""}
            onClick={() => {
              if (!isActivated) {
                setIsEditing(true);
              }
            }}
          >
            {isActivated ? (
              <>
                <ShieldCheck size={16} className="mr-1" />
                Activated
              </>
            ) : (
              <>
                <ShieldOff size={16} className="mr-1" />
                Activate
              </>
            )}
          </Button>
          
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <TrashIcon size={16} className="mr-1" />
            Remove
          </Button>
        </CardFooter>
      </Card>
      
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete MCP Service</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the "{service.name}" service? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                onDelete(service.id);
                setIsDeleteDialogOpen(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MCPServiceCard;
